import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    Button,
    Form,
    Grid,
    Header,
    Icon,
    Popup,
    Select,
} from "semantic-ui-react";
import {
    DateTimeUtils,
    generateResolver,
    Table,
    ViewMore,
    yup,
    AvatarGroup, 
    USER_PERMISSIONS, 
    VoicemailPlayback,
    PRODUCT_PERMISSIONS,
    ButtonLink,
    BannedIcon
} from "dyl-components";
import {
    KEY_CONTACT_ACCOUNT,
    KEY_DATE,
    KEY_PHONE_NUMBER,
    KEY_USER,
    KEY_DURATION,
    KEY_ACTIONS,
    KEY_DIRECTION,
    KEY_SYSTEM_RESULT,
    KEY_NOTE,
    KEY_USER_RESULT,
    KEY_DESTINATION,
    KEY_CALL_TAGS,
    KEY_CALL_TYPE,
} from "shared/constants/CALL_HISTORY_LOGS_COLUMNS";
import PropTypes from "prop-types";
import { Controller, useForm } from "react-hook-form";
import useModal from "utils/useModal";
import { PhoneUtil } from "utils";
import "./index.scss";
import { useDispatch, useSelector } from "react-redux";
import appActions from "actions/app/app";
import RestrictedView from "shared/RestrictedView";
import NewActivityForm from "../forms/NewActivity";

const ReportCallPopup = ({ entry, onClose, onReportCall }) => {
    const {
        control,
        handleSubmit,
        formState: { isDirty, isValid },
    } = useForm({
        defaultValues: {
            problem_type: "",
        },
        mode: "onChange",
        resolver: generateResolver({
            problem_type: yup.string().required("Problem type is required"),
        }),
    });

    const onSubmit = (data) => {
        onReportCall?.(entry, data);
    };

    return (
        <div
            style={{
                padding: ".5rem",
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
            }}
        >
            <Grid>
                <Grid.Row>
                    <Grid.Column width={13}>
                        <Header as="h3" color="blue">
                            Report Call Problem
                        </Header>
                    </Grid.Column>
                    <Grid.Column>
                        <Icon
                            name="close"
                            size="large"
                            floated="right"
                            className="fa-solid fa-x"
                            onClick={onClose}
                        />
                    </Grid.Column>
                </Grid.Row>
            </Grid>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Form.Field required>
                    <label>Problem Type</label>
                    <Controller
                        name="problem_type"
                        control={control}
                        rules={{ required: "Please select a problem type" }}
                        render={({ field, fieldState }) => (
                            <Select
                                {...field}
                                options={PhoneUtil.CALL_ISSUE_OPTIONS.getOptions()}
                                placeholder="Select Problem Type"
                                onChange={(_, { value }) =>
                                    field.onChange(value)
                                }
                                clearable
                                error={fieldState?.error?.message}
                            />
                        )}
                    />
                </Form.Field>
                <Button
                    disabled={!isValid || !isDirty}
                    floated="right"
                    type="submit"
                    primary
                >
                    Save
                </Button>
            </Form>
        </div>
    );
};

const CallHistoryRow = ({
    entry,
    isColumnHidden,
    onEditLog,
    onAddContact,
    onMarkVoicemail,
    onReportCall,
    root,
    isPhoneSystemSupported,
    onClickToDial,
    isTimeline
}) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const [isVoicemailPlaybackOpen, setIsVoicemailPlaybackOpen] =
        useState(false);
    const [isCallActivityFormOpen, setIsCallActivityFormOpen] = useState(false);

    const reportCallModal = useModal();
    const isSelfReportCall = entry?.type === "log_call";
    const account = useSelector((state) => state?.account?.account);
    const contact = useSelector((state) => state.contact?.contact);

    if (!entry?.system_result && entry?.result) {
        entry.system_result = entry.result;
    }

    if (!entry?.call_uuid && entry?.id) {
        entry.call_uuid = entry.id;
    }
    if (Object.keys(entry?.call ?? {}).length) {
        entry = { ...entry, ...entry?.call };
    }

    if (!entry?.note && entry?.notes) {
        entry.note = entry.notes;
    }

    if (!entry?.phone && entry?.record?.phone) {
        entry.phone = entry?.record?.phone;
    }

    if(!entry.phone && entry?.destination){
        entry.phone = entry?.destination;
    }

    if (!entry?.record?.contact_id && entry?.contact_id) {
        entry.record.contact_id = entry?.contact_id;
    }

    const onRecordCurrentRoute = () => {
        const path = location.pathname;
        const settings = path.split("/")[1];
        if (settings !== "settings") {
            dispatch(appActions.onSaveSettingsRoute(path));
        }
    };

    const call_type =
        entry?.call_recording &&
        (entry?.system_result === "Missed" ||
            entry?.system_result === "Missed Attempt")
            ? "voicemail"
            : entry?.call_recording
            ? "call_recording"
            : "";

    const callDirection = `fa-phone-arrow-${
        entry?.direction === "outbound" ? "up-right" : "down-left"
    }`;

    const call_suspended = entry?.record?.call_suspended;
    return (
        <Table.Row>
            <Table.Cell
                style={{ paddingLeft: 15 }}
                className={
                    entry?.unreturned
                        ? "CallHistoryTable__RedCellIndicator"
                        : ""
                }
                title={
                    (entry?.system_result === "Missed" ||
                        entry?.system_result === "Missed Attempt") &&
                    entry?.unreturned
                        ? "Call Not Returned"
                        : ""
                }
            />
            {!isColumnHidden(KEY_DATE) && (
                <Table.Cell>
                    {(entry?.start_ts || entry?.ts) && (
                        <>
                            <strong>
                                {DateTimeUtils.formatEpoch(
                                    entry.start_ts || entry?.ts,
                                    DateTimeUtils.WORD_DATE_FORMAT
                                )}
                            </strong>
                            <br />
                            {DateTimeUtils.formatEpoch(
                                entry.start_ts || entry?.ts,
                                DateTimeUtils.TIME_FORMAT
                            )}
                        </>
                    )}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_CONTACT_ACCOUNT) && (
                <Table.Cell>
                    {entry?.record?.name ? (
                        <>
                            {root === "account" ? (
                                <div style={{ display: "flex" }}>
                                    <AvatarGroup
                                        persons={entry?.contacts?.map(
                                            (contact) => ({
                                                ...contact,
                                                first_name: contact?.name,
                                                last_name: "",
                                            })
                                        )}
                                        limit={3}
                                        size="3em"
                                    />
                                </div>
                            ) : (
                                <>
                                    <Icon
                                        className={`grey fa-solid fa-${
                                            entry?.record?.contact_type ===
                                            "household"
                                                ? "house"
                                                : entry?.record
                                                      ?.contact_type ===
                                                  "business"
                                                ? "building"
                                                : "user"
                                        }`}
                                    />

                                    {(entry?.record?.account_id ||
                                    entry?.record?.contact_id ) && !entry?.record?.archived ? (
                                        <Link
                                            to={
                                                entry?.record?.contact_id
                                                    ? `/contact/${entry?.record?.contact_id}`
                                                    : entry?.record?.account_id
                                                    ? `/account/${entry?.record?.account_id}`
                                                    : ""
                                            }
                                        >
                                            {entry?.record?.name}
                                        </Link>
                                    ) : (
                                        entry?.record?.name
                                    )}
                                </>
                            )}
                        </>
                    ) : entry?.contacts[0]?.contact_name ? (
                        entry?.contacts[0]?.contact_name
                    ) : entry?.direction !== "internal" ? (
                        "Unknown"
                    ) : (
                        ""
                    )}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_PHONE_NUMBER) && (
                <Table.Cell width={2}>
                    {entry?.direction !== "internal"
                        ? PhoneUtil.formatPhoneNumber(entry?.phone)
                        : entry?.destination?.label}

                    {isPhoneSystemSupported ?
                        !call_suspended ? (
                            <ButtonLink disabled={entry?.record?.archived || call_suspended} onClick={(event) => {
                                event.stopPropagation();
                                const payload = entry?.contacts?.length > 0 ? {
                                    account_id: entry.contacts?.[0]?.account_id,
                                    contact_id: entry.contacts?.[0]?.contact_id,
                                    destination: entry.contacts?.[0]?.phone
                                } : {
                                    destination: entry?.destination?.fs_destination,
                                    to_user_id: entry?.destination?.user_id
                                }
                                onClickToDial?.(payload)
                            }}>
                                <Icon className='fa-solid fa-phone' />    
                            </ButtonLink>
                        ) : <BannedIcon iconName='fas fa-phone' primary style={{ marginLeft: "0.5em" }} />
                        : <Popup
                            className="AddForm"
                            trigger={
                                <ButtonLink>
                                    <Icon
                                        className="fa-solid fa-phone"
                                        primary
                                    />
                                </ButtonLink>
                            }
                            content={
                                <NewActivityForm
                                    contact={{
                                        id: entry?.record?.contact_id || entry?.record?.account_id,
                                    }}
                                    isContact={Boolean(entry?.record?.contact_id)}
                                    onClose={() => setIsCallActivityFormOpen(false)}
                                    hideActivitySelection
                                />}
                            closeOnDocumentClick={false}
                            open={isCallActivityFormOpen}
                            onClose={() => setIsCallActivityFormOpen(false)}
                            onOpen={() => setIsCallActivityFormOpen(true)}
                            on="click"
                            position="bottom left"
                            pinned
                            basic
                            wide
                        />}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_USER) && (
                <Table.Cell>
                    {entry?.users?.length > 0 && (
                        <AvatarGroup
                            persons={entry?.users?.map((user) => ({
                                ...user,
                                inactive: !user.active,
                            }))}
                            limit={3}
                            size="3em"
                            isLeftAlign
                        />
                    )}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_DIRECTION) && (
                <Table.Cell className="CallHistoryTable__Capitalize" width={1}>
                    {entry?.direction && (
                        <Icon
                            circular
                            className={`direction-icon fa-solid ${
                                entry?.direction === "internal"
                                    ? "fa-people-pants-simple"
                                    : callDirection
                            }`}
                        />
                    )}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_CALL_TYPE) && (
                <Table.Cell className="CallHistoryTable__Capitalize" width={1}>
                    {entry?.call_type}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_SYSTEM_RESULT) && (
                <Table.Cell
                    className={`CallHistoryTable__Capitalize ${
                        entry?.system_result === "Connected" ||
                        entry?.system_result === "Voicemail Drop" ||
                        entry?.system_result === "Connected - Warm Transfer" ||
                        entry?.system_result === "Connected - Blind Transfer" ||
                        entry?.system_result === "Conference" ||
                        entry?.system_result === "Completed"
                            ? "CallHistoryTable__SucessText"
                            : "CallHistoryTable__FailureText"
                    }`}
                    width={2}
                >
                    {PhoneUtil.SYSTEM_RESULT_OPTIONS.find(
                        (option) => option?.value === entry?.system_result
                    )?.text || entry?.system_result}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_USER_RESULT) && !isTimeline && (
                <Table.Cell
                    className={`CallHistoryTable__Capitalize ${
                        entry?.user_result === "Not Reached" ||
                        entry?.user_result === "Voicemail Not Left"
                            ? "CallHistoryTable__FailureText"
                            : "CallHistoryTable__SucessText"
                    }`}
                    width={2}
                >
                    {entry?.user_result}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_DURATION) && (
                <Table.Cell className="CallHistoryTable__ActionColumn">
                    {call_type && (
                        <Popup
                            style={{ minWidth: 300 }}
                            trigger={
                                <Icon
                                    className={`blue fa-solid fa-${
                                        call_type === "call_recording"
                                            ? "circle-play"
                                            : "voicemail"
                                    }`}
                                    onClick={() =>
                                        setIsVoicemailPlaybackOpen(true)
                                    }
                                    disabled={!entry?.call_recording}
                                    title={
                                        call_type === "call_recording"
                                            ? "Play Call Recording"
                                            : "Play Voicemail"
                                    }
                                />
                            }
                            on="click"
                            content={
                                <VoicemailPlayback
                                    onClose={() =>
                                        setIsVoicemailPlaybackOpen(false)
                                    }
                                    className={`audio-playback-${entry?.call_uuid}`}
                                    fileName={
                                        call_type === "call_recording"
                                            ? "Call Recording"
                                            : "Voicemail"
                                    }
                                    link={entry?.call_recording}
                                    isVoicemail={call_type === "voicemail"}
                                    onMarkAsListened={() => {
                                        if (entry?.listened) return;
                                        onMarkVoicemail(
                                            entry?.call_uuid,
                                            true,
                                            false
                                        );
                                    }}
                                />
                            }
                            position="bottom center"
                            open={isVoicemailPlaybackOpen}
                            onOpen={() => setIsVoicemailPlaybackOpen(true)}
                        />
                    )}
                    <span className="CallHistoryTable__TimeContainer">
                        {entry?.length
                            ? PhoneUtil.timeTicker(entry?.length)
                            : "00:00:00"}
                    </span>
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_DESTINATION) && (
                <Table.Cell className="CallHistoryTable__SmallFixedWidth">
                    {PhoneUtil.DESTINATION_TYPES_ICONS[
                        entry?.destination?.type
                    ] && (
                        <Icon
                            className={
                                PhoneUtil.DESTINATION_TYPES_ICONS[
                                    entry?.destination?.type
                                ]
                            }
                        />
                    )}{" "}
                    {PhoneUtil.DESTINATION_TYPES_URLS[
                        entry?.destination?.type
                    ] ? (
                        <Link
                            onClick={onRecordCurrentRoute}
                            to={`${
                                PhoneUtil.DESTINATION_TYPES_URLS[
                                    entry?.destination?.type
                                ]
                            }?search=${
                                entry?.destination?.type === "phone_number"
                                    ? entry?.destination?.alias
                                    : entry?.destination?.type ===
                                      "personal_voicemail"
                                    ? entry?.destination?.fs_destination?.split(
                                          "_"
                                      )[1]
                                    : entry?.destination?.label
                            }${
                                entry?.destination?.type === "call_queue"
                                    ? "&type=call_queue"
                                    : entry?.destination?.type === "ring_group"
                                    ? "&type=ring_group"
                                    : entry?.destination?.type ===
                                      "company_voicemail"
                                    ? "&type=company_voicemail"
                                    : entry?.destination?.type ===
                                      "conference_room"
                                    ? "&type=conference_room"
                                    : entry?.destination?.type === "parking_lot"
                                    ? "&type=parking_lot"
                                    : ""
                            }`}
                        >
                            {entry?.destination?.label}
                        </Link>
                    ) : entry?.destination?.label ? (
                        entry?.destination?.label
                    ) : ( PhoneUtil.isUSPhoneNumber(entry?.destination) ? PhoneUtil.formatPhoneNumber(entry.destination) : entry?.destination )}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_CALL_TAGS) && (
                <Table.Cell
                    className="CallHistoryTable__MediumFixedWidth"
                    width={2}
                >
                    {entry?.tags?.map((t) => t?.name)?.join("/ ")}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_NOTE) && (
                <Table.Cell
                    className="CallHistoryTable__LargeFixedWidth"
                    width={2}
                >
                    {entry?.note && <ViewMore text={entry.note} />}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_ACTIONS) && (
                <Table.Cell className="CallHistoryTable__SmallFixedWidth CallHistoryTable__ActionColumn">
                    <Popup
                        trigger={
                            <RestrictedView type="disabled" userPermission={root === "account" ? USER_PERMISSIONS.ACCOUNT_EDIT : root === "contact" ? USER_PERMISSIONS.CONTACTS_EDIT : false} userRestrict assignmentIds={root === "account" ? [(account?.assignees?.owner_id || 0), (account?.assignees?.coowner_id || 0)] : [(contact?.assignees?.owner_id || 0), (contact?.assignees?.coowner_id || 0)]}>
                                <Icon
                                    onClick={() => onEditLog?.(entry)}
                                    className="fa-solid fa-pencil blue"
                                    title="Edit"
                                    disabled={entry?.direction === "internal" || entry?.system_result === "Missed" || entry?.system_result === "Missed Attempt" || isSelfReportCall}
                                />
                            </RestrictedView>
                        }
                        content="Edit"
                        position="top center"
                        inverted
                    />
                    <Popup
                        style={{ minWidth: 310 }}
                        trigger={
                            <RestrictedView type="disabled" userPermission={root === "account" ? USER_PERMISSIONS.ACCOUNT_EDIT : root === "contact" ? USER_PERMISSIONS.CONTACTS_EDIT : false} userRestrict assignmentIds={root === "account" ? [(account?.assignees?.owner_id || 0), (account?.assignees?.coowner_id || 0)] : [(contact?.assignees?.owner_id || 0), (contact?.assignees?.coowner_id || 0)]}>
                                <Icon
                                    className="fa-solid fa-warning blue"
                                    title="Report Call Problem"
                                />
                            </RestrictedView>
                        }
                        on="click"
                        content={
                            <ReportCallPopup
                                entry={entry}
                                onClose={reportCallModal?.closeModal}
                                onReportCall={onReportCall}
                            />
                        }
                        position="bottom right"
                        open={reportCallModal?.isVisible}
                        onOpen={reportCallModal?.openModal}
                        onClose={reportCallModal?.closeModal}
                    />
                    {call_type === "voicemail" && (
                        <Popup
                            trigger={
                                <RestrictedView type="disabled" userPermission={root === "account" ? USER_PERMISSIONS.ACCOUNT_EDIT : root === "contact" ? USER_PERMISSIONS.CONTACTS_EDIT : false} userRestrict assignmentIds={root === "account" ? [(account?.assignees?.owner_id || 0), (account?.assignees?.coowner_id || 0)] : [(contact?.assignees?.owner_id || 0), (contact?.assignees?.coowner_id || 0)]}>
                                    <Icon
                                        className={`fa-solid blue ${
                                            entry?.listened
                                                ? "fa-circle"
                                                : "fa-circle-dashed"
                                        }`}
                                        onClick={() =>
                                            onMarkVoicemail?.(
                                                entry?.call_uuid,
                                                !!!entry?.listened
                                            )
                                        }
                                    />
                                </RestrictedView>
                            }
                            content={
                                entry?.listened
                                    ? "Mark as Unlistened"
                                    : "Mark as Listened"
                            }
                            position="top center"
                            inverted
                        />
                    )}
                    {!isSelfReportCall &&
                        !entry?.record?.account_id &&
                        !entry?.record?.contact_id &&
                        entry?.direction !== "internal" && (
                            <RestrictedView type="disabled" productPermission={PRODUCT_PERMISSIONS.RECORDS} userPermission={[(contact?.assignees?.owner_id || 0), (contact?.assignees?.coowner_id || 0)]}>
                                <Icon
                                    onClick={() => onAddContact?.(entry)}
                                    className="fa-solid fa-circle-plus blue"
                                    title="Add Contact"
                                />
                            </RestrictedView>
                        )}
                </Table.Cell>
            )}
        </Table.Row>
    );
};

CallHistoryRow.propTypes = {
    entry: PropTypes.object.isRequired,
    isColumnHidden: PropTypes.func.isRequired,
    onEditLog: PropTypes.func,
    onAddContact: PropTypes.func,
    onMarkVoicemail: PropTypes.func,
    onReportCall: PropTypes.func,
    root: PropTypes.string,
};

export default CallHistoryRow;
